import React, { useState, useMemo } from 'react';
import { useParams, Link, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { ALL_DESIGNERS, slugify, listingDesignerKey } from '../../data/designers';
import { getListingLabelMap } from '../../config/configListing';
import { createSlug } from '../../util/urlHelpers';
import css from './CollectionPage.module.css';
import {
  PRICE_MIN_DEFAULT,
  PRICE_MAX_DEFAULT,
  COLOR_OPTIONS,
  SIZE_OPTIONS,
  FABRIC_OPTIONS,
  INCLUDED_OPTIONS,
} from '../../config/dhobaraFilters';

// Dhobara: keyed by the value listings actually store in publicData.designer,
// not by slugify(name) — five legacy designers differ between the two, and
// building this from slugify() made their cards render the raw slug ("hsy")
// instead of the designer's name.
const DESIGNER_LABEL_MAP = getListingLabelMap('designer');

const COLLECTION_LABEL_MAP = {
  bridal: 'Bridal',
  formals: 'Formal',
  formal: 'Formal',
  casual: 'Casual',
  mens: 'Mens',
  kids: 'Kids',
};

// ── Listing card ─────────────────────────────────────────────────────────────

function ListingCard({ listing }) {
  const attrs = listing.attributes || {};
  const title = attrs.title || '';
  const price = attrs.price;
  const pub = attrs.publicData || {};
  const designerSlug = pub.designer;
  const designerLabel = DESIGNER_LABEL_MAP[designerSlug] || designerSlug || '';
  const collection = pub.collection;

  const priceStr = price
    ? `$${(price.amount / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`
    : null;

  const images = listing.images || [];
  const firstImage = images[0];
  const imgUrl =
    firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
    firstImage?.attributes?.variants?.['listing-card']?.url ||
    firstImage?.attributes?.variants?.['scaled-medium']?.url ||
    null;

  const slug = createSlug(title);
  const id = listing.id?.uuid;

  const collectionLabel = collection && COLLECTION_LABEL_MAP[collection]
    ? COLLECTION_LABEL_MAP[collection]
    : null;

  const sold = isSoldOut(listing);

  return (
    <Link to={`/l/${slug}/${id}`} className={css.card}>
      <div className={css.cardImage}>
        {imgUrl ? (
          <img
            src={imgUrl}
            alt={designerLabel || title}
            loading="lazy"
            className={sold ? css.cardImageSold : undefined}
          />
        ) : (
          <div className={css.cardImagePlaceholder} />
        )}
        {sold && <span className={css.soldBadge}>Sold</span>}
      </div>
      {designerLabel && <p className={css.cardDesigner}>{designerLabel.toUpperCase()}</p>}
      <p className={css.cardGarment}>{title}</p>
      <div className={css.cardMeta}>
        {priceStr && <span className={css.cardPrice}>{priceStr}</span>}
        {collectionLabel && (
          <span className={css.cardCollection}>{collectionLabel.toUpperCase()}</span>
        )}
      </div>
    </Link>
  );
}

import bridalImg from '../../assets/dhobara-hero-bridal.jpg';
import { isSoldOut } from '../../util/soldOut';
import formalImg from '../../assets/dhobara-formal-attire.jpg';
import casualImg from '../../assets/dhobara-sage-pink-dupatta.jpg';
import mensImg from '../../assets/dhobara-mens-sherwani-ivory.jpg';
import kidsImg from '../../assets/dhobara-cream-kurta.jpg';

const COLLECTIONS = {
  bridal: {
    title: 'Bridal',
    description:
      "Authenticated lehengas, sherwanis, and occasion sets from South Asia's most celebrated designers — worn once, loved forever.",
    image: bridalImg,
    imageAlt: 'South Asian bridal wear',
    imagePosition: 'center center',
    searchKey: 'bridal',
  },
  formal: {
    title: 'Formal',
    description:
      'Embroidered anarkalis, silk kurtas, and tailored sherwanis from designers who understand elegance.',
    image: formalImg,
    imageAlt: 'South Asian formal attire',
    imagePosition: 'center 30%',
    searchKey: 'formal',
  },
  casual: {
    title: 'Casual',
    description:
      "Pre-loved lawn sets, linen kurtas, and everyday separates from South Asia's top designers.",
    image: casualImg,
    imageAlt: 'Casual South Asian wear',
    // Slightly right of centre so the pink dupatta stays in frame rather than
    // the empty dark side.
    imagePosition: '62% center',
    searchKey: 'casual',
  },
  mens: {
    title: "Mens",
    description:
      "From intricately embroidered sherwanis to understated kurta sets — the best of South Asian menswear, authenticated and pre-owned.",
    image: mensImg,
    imageAlt: 'South Asian menswear',
    // The sherwani frame is nearly square; the hero is very wide, so it crops
    // hard vertically. Biasing to the top keeps the mandarin collar and the
    // top pearl button in shot instead of a field of beadwork.
    imagePosition: 'center 12%',
    searchKey: 'mens',
  },
  kids: {
    title: "Kids",
    description:
      "Pre-loved designer pieces — outgrown, not overworn — ready for their next family.",
    image: kidsImg,
    imageAlt: 'Designer kids wear',
    imagePosition: 'center center',
    searchKey: 'kids',
  },
};

const FILTER_SECTIONS = [
  { key: 'designer', label: 'Designer', type: 'designers' },
  { key: 'price', label: 'Price Range', type: 'range', min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT, prefix: '$' },
  { key: 'color', label: 'Color', type: 'checkboxes', options: COLOR_OPTIONS },
  { key: 'size', label: 'Size', type: 'checkboxes', options: SIZE_OPTIONS },
  { key: 'fabric', label: 'Fabric', type: 'checkboxes', options: FABRIC_OPTIONS },
  { key: 'included', label: "What's Included", type: 'checkboxes', options: INCLUDED_OPTIONS },
];

const IconFilter = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="18" height="18">
    <path d="M3 5h14M6 10h8M9 15h2" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="18" height="18">
    <path d="M4 4l12 12M16 4L4 16" />
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="14" height="14">
    <path d="M8 2v12M2 8h12" />
  </svg>
);

const IconMinus = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="14" height="14">
    <path d="M2 8h12" />
  </svg>
);

const FilterRow = ({
  section,
  selectedValues,
  onToggle,
  selectedDesigners,
  onToggleDesigner,
  priceMin,
  priceMax,
  onPriceMinChange,
  onPriceMaxChange,
}) => {
  const [open, setOpen] = useState(false);
  const [searchVal, setSearchVal] = useState('');

  const filteredDesigners = section.type === 'designers'
    ? ALL_DESIGNERS.filter(d => d.toLowerCase().includes(searchVal.toLowerCase()))
    : [];

  return (
    <div className={css.filterRow}>
      <button type="button" className={css.filterRowToggle} onClick={() => setOpen(o => !o)}>
        <span>{section.label}</span>
        {open ? <IconMinus /> : <IconPlus />}
      </button>

      {open && (
        <div className={css.filterRowBody}>
          {section.type === 'designers' && (
            <>
              <input
                type="text"
                className={css.designerSearch}
                placeholder="Search designers…"
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
              />
              <div className={css.designerList}>
                {filteredDesigners.length === 0 ? (
                  <span className={css.designerNoResults}>No designers found</span>
                ) : (
                  filteredDesigners.map(d => {
                    // The checkbox value has to BE the stored publicData
                    // .designer value, since that's what it's filtered
                    // against below — hence listingDesignerKey().
                    const slug = listingDesignerKey(slugify(d));
                    return (
                      <label key={d} className={css.filterCheckboxLabel}>
                        <input
                          type="checkbox"
                          className={css.filterCheckbox}
                          checked={selectedDesigners.has(slug)}
                          onChange={() => onToggleDesigner(slug)}
                        />
                        <span>{d}</span>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}

          {section.type === 'range' && (
            <div className={css.filterRangeInputs}>
              <div className={css.filterRangeField}>
                <span className={css.filterRangePrefix}>{section.prefix}</span>
                <input
                  type="number"
                  className={css.filterRangeInput}
                  value={priceMin}
                  min={section.min}
                  max={priceMax}
                  onFocus={e => e.target.select()}
                  onChange={e => onPriceMinChange(Number(e.target.value))}
                />
              </div>
              <span className={css.filterRangeSep}>–</span>
              <div className={css.filterRangeField}>
                <span className={css.filterRangePrefix}>{section.prefix}</span>
                <input
                  type="number"
                  className={css.filterRangeInput}
                  value={priceMax}
                  min={priceMin}
                  max={section.max}
                  onFocus={e => e.target.select()}
                  onChange={e => onPriceMaxChange(Number(e.target.value))}
                />
              </div>
            </div>
          )}

          {section.type === 'checkboxes' && (
            <div className={css.filterCheckboxes}>
              {section.options.map(opt => (
                <label key={opt.value} className={css.filterCheckboxLabel}>
                  <input
                    type="checkbox"
                    className={css.filterCheckbox}
                    checked={selectedValues.has(opt.value)}
                    onChange={() => onToggle(section.key, opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const EMPTY_FILTERS = {
  designers: new Set(),
  price: { min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT },
  color: new Set(),
  size: new Set(),
  fabric: new Set(),
  included: new Set(),
};

const CollectionPage = () => {
  const { slug } = useParams();
  const config = COLLECTIONS[slug];
  const [filterOpen, setFilterOpen] = useState(false);

  // Pending filter state (inside the drawer, not yet applied)
  const [pendingDesigners, setPendingDesigners] = useState(new Set());
  const [pendingPrice, setPendingPrice] = useState({ min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT });
  const [pendingCheckboxes, setPendingCheckboxes] = useState({
    color: new Set(), size: new Set(), fabric: new Set(), included: new Set(),
  });

  // Applied filter state (what actually filters the list)
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);

  const toggleCheckbox = (key, value) => {
    setPendingCheckboxes(prev => {
      const next = new Set(prev[key]);
      if (next.has(value)) next.delete(value); else next.add(value);
      return { ...prev, [key]: next };
    });
  };

  const toggleDesigner = slug => {
    setPendingDesigners(prev => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  };

  const handleApply = () => {
    setAppliedFilters({
      designers: new Set(pendingDesigners),
      price: { ...pendingPrice },
      color: new Set(pendingCheckboxes.color),
      size: new Set(pendingCheckboxes.size),
      fabric: new Set(pendingCheckboxes.fabric),
      included: new Set(pendingCheckboxes.included),
    });
    setFilterOpen(false);
  };

  const handleClearAll = () => {
    setPendingDesigners(new Set());
    setPendingPrice({ min: PRICE_MIN_DEFAULT, max: PRICE_MAX_DEFAULT });
    setPendingCheckboxes({ color: new Set(), size: new Set(), fabric: new Set(), included: new Set() });
    setAppliedFilters(EMPTY_FILTERS);
  };

  // Redux listings
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  const { inProgress, currentPageResultIds = [] } = useSelector(
    state => state.CollectionPage || {}
  );
  const allListings = useSelector(state => selectListingsById(state, currentPageResultIds));

  // Client-side filtering
  const listings = useMemo(() => {
    const { designers, price, color, size, fabric, included } = appliedFilters;
    const hasFilters =
      designers.size || color.size || size.size || fabric.size || included.size ||
      price.min > PRICE_MIN_DEFAULT || price.max < PRICE_MAX_DEFAULT;
    if (!hasFilters) return allListings;

    return allListings.filter(listing => {
      const pub = listing.attributes?.publicData || {};
      const listingPrice = (listing.attributes?.price?.amount || 0) / 100;
      if (designers.size && !designers.has(pub.designer)) return false;
      if (listingPrice < price.min || listingPrice > price.max) return false;
      if (color.size && !color.has(pub.color)) return false;
      if (size.size && !size.has(pub.size)) return false;
      if (fabric.size && !fabric.has(pub.fabric)) return false;
      if (included.size) {
        const garments = pub.garmentType || [];
        if (!Array.from(included).some(g => garments.includes(g))) return false;
      }
      return true;
    });
  }, [allListings, appliedFilters]);

  if (!config) return <Redirect to="/not-found" />;

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${config.title} Collection | Dhobara`,
        description: config.description,
        url: `https://dhobara.com/collection/${slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dhobara.com' },
          { '@type': 'ListItem', position: 2, name: 'Collections', item: 'https://dhobara.com/collection' },
          { '@type': 'ListItem', position: 3, name: config.title, item: `https://dhobara.com/collection/${slug}` },
        ],
      },
      ...(listings.length > 0
        ? [{
            '@type': 'ItemList',
            name: `${config.title} Collection on Dhobara`,
            numberOfItems: listings.length,
            itemListElement: listings.slice(0, 20).map((l, i) => {
              const lTitle = l.attributes?.title || '';
              const lSlug = createSlug(lTitle);
              const lId = l.id?.uuid;
              return {
                '@type': 'ListItem',
                position: i + 1,
                name: lTitle,
                url: `https://dhobara.com/l/${lSlug}/${lId}`,
              };
            }),
          }]
        : []),
    ],
  };

  return (
    <Page title={`${config.title} Collection | Dhobara`} description={config.description}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }} />
      <DhobaraPageLayout>

        {/* HERO */}
        <section className={css.hero}>
          <img src={config.image} alt={config.imageAlt} className={css.heroImg} style={{ objectPosition: config.imagePosition }} />
          <div className={css.heroOverlay} />
          <div className={css.heroContent}>
            <span className={css.eyebrow}>Collection</span>
            <h1 className={css.collectionName}>{config.title}</h1>
            <p className={css.description}>{config.description}</p>
          </div>
        </section>

        {/* LISTINGS */}
        <section className={css.body}>
          <div className={css.bodyInner}>

            {/* Top bar: filter button */}
            <div className={css.listingBar}>
              <button type="button" className={css.filterBtn} onClick={() => setFilterOpen(true)}>
                <IconFilter />
                <span>Filter</span>
              </button>
            </div>

            {/* Listings */}
            {inProgress ? (
              <div className={css.loadingState}>
                <p className={css.loadingText}>Loading…</p>
              </div>
            ) : listings.length > 0 ? (
              <div className={css.grid}>
                {listings.map(listing => (
                  <ListingCard key={listing.id?.uuid} listing={listing} />
                ))}
              </div>
            ) : (
              <div className={css.emptyState}>
                <p className={css.emptyText}>No listings here yet — but they're coming.</p>
                <p className={css.emptySubText}>
                  Check back soon, or be the first to list a piece in this collection.
                </p>
                <Link to="/dashboard" className={css.emptyBtn}>Submit a Listing</Link>
              </div>
            )}
          </div>
        </section>

        {/* FILTER DRAWER */}
        {filterOpen && (
          <div className={css.drawerBackdrop} onClick={() => setFilterOpen(false)} />
        )}
        <div className={filterOpen ? `${css.drawer} ${css.drawerOpen}` : css.drawer}>
          <div className={css.drawerHeader}>
            <span className={css.drawerTitle}>Filter</span>
            <button type="button" className={css.drawerClose} onClick={() => setFilterOpen(false)}>
              <IconClose />
            </button>
          </div>

          <div className={css.drawerBody}>
            {FILTER_SECTIONS.map(s => (
              <FilterRow
                key={s.key}
                section={s}
                selectedValues={pendingCheckboxes[s.key] || new Set()}
                onToggle={toggleCheckbox}
                selectedDesigners={pendingDesigners}
                onToggleDesigner={toggleDesigner}
                priceMin={pendingPrice.min}
                priceMax={pendingPrice.max}
                onPriceMinChange={min => setPendingPrice(p => ({ ...p, min }))}
                onPriceMaxChange={max => setPendingPrice(p => ({ ...p, max }))}
              />
            ))}
          </div>

          <div className={css.drawerFooter}>
            <button type="button" className={css.drawerClear} onClick={handleClearAll}>Clear All</button>
            <button type="button" className={css.drawerApply} onClick={handleApply}>Apply</button>
          </div>
        </div>

        {/* COLLECTION NAV */}
        <section className={css.collectionNav}>
          <div className={css.collectionNavInner}>
            <span className={css.collectionNavLabel}>Explore Other Collections</span>
            <div className={css.collectionNavLinks}>
              {Object.entries(COLLECTIONS)
                .filter(([key]) => key !== slug)
                .map(([key, c]) => (
                  <Link key={key} to={`/collection/${key}`} className={css.collectionNavItem}>
                    {c.title}
                  </Link>
                ))}
              <Link to="/s" className={css.collectionNavItem}>Browse All</Link>
            </div>
          </div>
        </section>

      </DhobaraPageLayout>
    </Page>
  );
};

export default CollectionPage;
